import React, { useRef, useState } from "react";
import { Checkbox, Popover } from "@mui/material";
import { Columns3, GripVertical } from "lucide-react";
import { DETAILS_COLUMN_ID, labelForColumnId } from "./callsTableConfig";

const reorderColumns = (order, dragId, overId) => {
  const withoutDetails = order.filter((id) => id !== DETAILS_COLUMN_ID);
  const from = withoutDetails.indexOf(dragId);
  const to = withoutDetails.indexOf(overId);
  if (from < 0 || to < 0 || from === to) return order;

  const next = [...withoutDetails];
  next.splice(from, 1);
  next.splice(to, 0, dragId);
  return [...next, DETAILS_COLUMN_ID];
};

const CallsColumnMenu = ({ table, columnOrder, onColumnOrderChange }) => {
  const [anchorEl, setAnchorEl] = useState(null);
  const [dragId, setDragId] = useState(null);
  // Keep the active drag id in a ref so dragOver handlers always see the
  // originally dragged column (not the hover target).
  const dragIdRef = useRef(null);

  const open = Boolean(anchorEl);

  const orderedColumns = columnOrder
    .filter((id) => id !== DETAILS_COLUMN_ID)
    .map((id) => table.getColumn(id))
    .filter(Boolean);

  const handleDragStart = (e, id) => {
    // Required for drag to work in Firefox
    e.dataTransfer.effectAllowed = "move";
    e.dataTransfer.setData("text/plain", id);
    dragIdRef.current = id;
    setDragId(id);
  };

  const handleDragOver = (e, overId) => {
    e.preventDefault();
    e.dataTransfer.dropEffect = "move";
    const activeId = dragIdRef.current;
    if (!activeId || activeId === overId) return;

    onColumnOrderChange((prev) => reorderColumns(prev, activeId, overId));
  };

  const handleDrop = (e) => {
    e.preventDefault();
  };

  const handleDragEnd = () => {
    dragIdRef.current = null;
    setDragId(null);
  };

  return (
    <>
      <button
        type="button"
        onClick={(e) => setAnchorEl(e.currentTarget)}
        className="inline-flex items-center gap-2 px-3 py-2 text-sm font-medium text-gray-700 bg-white border border-gray-200 rounded-lg hover:bg-gray-50"
      >
        <Columns3 className="w-4 h-4" />
        Columns
      </button>
      <Popover
        open={open}
        anchorEl={anchorEl}
        onClose={() => setAnchorEl(null)}
        anchorOrigin={{ vertical: "bottom", horizontal: "left" }}
        transformOrigin={{ vertical: "top", horizontal: "left" }}
        slotProps={{
          paper: {
            sx: {
              mt: 1,
              borderRadius: "10px",
              border: "1px solid #E5E7EB",
              boxShadow: "0 8px 24px rgba(0,0,0,0.08)",
              minWidth: 260,
              maxHeight: 360,
            },
          },
        }}
      >
        <div className="px-3 py-2 border-b border-gray-100 text-xs font-medium text-gray-500 uppercase tracking-wide">
          Show / reorder columns
        </div>
        <div className="py-1 overflow-y-auto max-h-[300px]">
          {orderedColumns.map((column) => (
            <div
              key={column.id}
              onDragOver={(e) => handleDragOver(e, column.id)}
              onDrop={handleDrop}
              className={`flex items-center gap-1 px-2 py-1.5 hover:bg-gray-50 ${
                dragId === column.id ? "bg-indigo-50 opacity-80" : ""
              }`}
            >
              <span
                draggable
                onDragStart={(e) => handleDragStart(e, column.id)}
                onDragEnd={handleDragEnd}
                className="cursor-grab active:cursor-grabbing touch-none p-0.5"
                aria-label={`Drag to reorder ${labelForColumnId(column.id)}`}
              >
                <GripVertical className="w-4 h-4 text-gray-400 shrink-0" />
              </span>
              <Checkbox
                size="small"
                checked={column.getIsVisible()}
                onChange={column.getToggleVisibilityHandler()}
                disabled={!column.getCanHide()}
                sx={{ p: 0.5 }}
              />
              <span className="text-sm text-gray-800">
                {labelForColumnId(column.id)}
              </span>
            </div>
          ))}
        </div>
      </Popover>
    </>
  );
};

export default CallsColumnMenu;
